/**
 * 🚀 SPACE LAYOUT - Structure principale du cockpit
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Volume2, VolumeX } from 'lucide-react';
import SpaceNav from './SpaceNav';

// Fond spatial avec étoiles
const StarField = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Étoiles statiques */}
    {[...Array(100)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-white rounded-full opacity-30"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
    {/* Nébuleuse */}
    <div 
      className="absolute w-96 h-96 rounded-full opacity-10 blur-3xl"
      style={{
        background: 'radial-gradient(circle, rgba(0,212,255,0.3) 0%, transparent 70%)',
        left: '10%',
        top: '20%',
      }}
    />
    <div 
      className="absolute w-64 h-64 rounded-full opacity-10 blur-3xl"
      style={{
        background: 'radial-gradient(circle, rgba(168,85,247,0.3) 0%, transparent 70%)',
        right: '15%',
        bottom: '30%',
      }}
    />
  </div>
);

// Header du cockpit
const SpaceHeader = ({ title, subtitle, actions }) => (
  <motion.div 
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center justify-between mb-6"
  >
    <div>
      <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
        {title}
      </h1>
      {subtitle && <p className="text-gray-500 text-sm">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-3">{actions}</div>}
  </motion.div>
);

// Bouton de contrôle des sons
const SoundControl = ({ enabled, onToggle }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onToggle}
    className={`
      fixed bottom-4 right-4 z-50
      w-12 h-12 rounded-full
      flex items-center justify-center
      backdrop-blur-xl border-2 transition-all
      ${enabled 
        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' 
        : 'bg-gray-800/50 border-gray-600 text-gray-500'
      }
    `}
    title={enabled ? 'Sons activés 🔊' : 'Sons désactivés 🔇'}
  >
    {enabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
  </motion.button>
);

// Layout principal
const SpaceLayout = ({ 
  children, 
  activeModule, 
  onModuleChange,
  badges,
  onLogout,
  soundsEnabled,
  onToggleSounds
}) => {
  const [navCollapsed, setNavCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Fond spatial */}
      <StarField />
      
      {/* Ligne de scan */}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }
        @keyframes scanLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
      `}</style>
      
      {/* Navigation */}
      <SpaceNav
        activeModule={activeModule}
        onModuleChange={onModuleChange}
        badges={badges}
        onLogout={onLogout}
        collapsed={navCollapsed}
        onToggleCollapse={() => setNavCollapsed(!navCollapsed)}
      />
      
      {/* Contenu principal */}
      <main className="flex-1 overflow-y-auto relative z-10">
        <div className="p-6 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
      
      {/* Contrôle des sons */}
      {onToggleSounds && (
        <SoundControl enabled={soundsEnabled} onToggle={onToggleSounds} />
      )}
    </div>
  );
};

export { SpaceLayout, SpaceHeader, StarField };
export default SpaceLayout;
